import { useState, useEffect, useCallback, useRef } from 'react';

export type ApiStatus = 'idle' | 'loading' | 'success' | 'error';

export interface ApiState<T> {
  data: T | null;
  status: ApiStatus;
  error: string | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

export interface ApiActions {
  refetch: () => void;
}

type UseApiReturn<T> = ApiState<T> & ApiActions;

export function useApi<T>(
  url: string | null,
  options?: RequestInit
): UseApiReturn<T> {
  const [state, setState] = useState<Pick<ApiState<T>, 'data' | 'status' | 'error'>>({
    data: null,
    status: 'idle',
    error: null,
  });

  const abortRef = useRef<AbortController | null>(null);
  const fetchRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!url) {
      // Caller short-circuited (e.g. spread tab when near === far). Reset
      // state so consumers don't render stale data from the previous URL.
      if (abortRef.current) {
        abortRef.current.abort();
      }
      ++fetchRef.current;
      setState({
        data: null,
        status: 'idle',
        error: null,
      });
      return;
    }

    // Abort previous
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const fetchId = ++fetchRef.current;

    setState(s => ({
      ...s,
      status: 'loading',
      error: null,
    }));

    try {
      const { signal: _ignoreSignal, ...restOptions } = options ?? {};
      const res = await fetch(url, {
        credentials: 'include',
        ...restOptions,
        signal: controller.signal,
      });

      if (fetchId !== fetchRef.current) return;

      if (!res.ok) {
        const body = await res.json() as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }

      const data = await res.json() as T;
      if (fetchId !== fetchRef.current) return;

      setState({
        data,
        status: 'success',
        error: null,
      });
    } catch (err) {
      if (fetchId !== fetchRef.current) return;
      if ((err as Error).name === 'AbortError') return;

      setState({
        data: null,
        status: 'error',
        error: (err as Error).message,
      });
    }
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void fetchData();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetchData]);

  return {
    ...state,
    isLoading: state.status === 'loading',
    isSuccess: state.status === 'success',
    isError: state.status === 'error',
    refetch: fetchData,
  };
}
